<template>
  <SkinnyBanner page="landing" />
  <RevContainer class="mb-16 mt-32" data-spec="body">
    <h1 class="heading-1">{{ company }}</h1>

    <MainSellerInfo :average-rate :country :country-code :years="ageInYears" />

    <RevTabs
      class="mt-32"
      :label="i18n(translations.sellerPageTabsLabel)"
      scrollable
      variant="flexible"
    >
      <RevTabItem
        :active="activeTab === 'reviews'"
        :label="i18n(translations.sellerPageReviewsTab)"
        @click="handleTabChange('reviews')"
      />
      <RevTabItem
        :active="activeTab === 'items'"
        :label="i18n(translations.sellerPageItemsTab)"
        @click="handleTabChange('items')"
      />
      <RevTabItem
        :active="activeTab === 'about'"
        :label="i18n(translations.sellerPageAboutTab)"
        @click="handleTabChange('about')"
      />
    </RevTabs>

    <ReviewsTab v-show="activeTab === 'reviews'" :rating />
    <ItemsTab v-show="activeTab === 'items' && company" :company />
    <AboutTab
      v-show="activeTab === 'about' && sellerInfo"
      :country
      :country-code
      :seller-info
    />
  </RevContainer>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevContainer } from '@ds/components/Container'
import { RevTabItem } from '@ds/components/TabItem'
import { RevTabs } from '@ds/components/Tabs'

import SkinnyBanner from '~/scopes/branding/components/SkinnyBanner/SkinnyBanner.vue'
import { useSkinnyBannerLoader } from '~/scopes/branding/components/SkinnyBanner/useSkinnyBanner'

import translations from './SellerPage.translations'
import AboutTab from './components/AboutTab/AboutTab.vue'
import ItemsTab from './components/ItemsTab/ItemsTab.vue'
import MainSellerInfo from './components/MainSellerInfo/MainSellerInfo.vue'
import ReviewsTab from './components/ReviewsTab/ReviewsTab.vue'
import { useSellerInfo } from './composables/useSellerInfo'
import { useSellerRating } from './composables/useSellerRating'

const i18n = useI18n()
const activeTab = ref('reviews')
const { load: loadSkinnyBannerData } = useSkinnyBannerLoader()
const tracking = useTracking()
const route = useRoute()
const routeName = route.name?.toString() || ''

await loadSkinnyBannerData()

const { data: sellerInfo } = useSellerInfo()
const { data: rating } = useSellerRating()

const company = computed(() => sellerInfo.value?.company)
const countryCode = computed(() => sellerInfo.value?.countryCode)
const country = computed(() => sellerInfo.value?.country)
const averageRate = computed(() => rating.value?.averageRate)

// For seller with ageInMonths < 12, we return 1 year instead of 0
const ageInYears = computed(
  () => Math.floor((sellerInfo.value?.delta_aged ?? 0) / 12) || 1,
)

const handleTabChange = (tab: string) => {
  activeTab.value = tab
  tracking.trackClick({
    pageType: routeName,
    zone: tab,
  })
}
</script>
